import { useCallback, useEffect, useRef, useState } from "react";
import * as defaultApi from "../api/coachApi";

/*
 * The athlete's memories for Coach settings (contract §5.5): one list, kept in
 * the server's order (active first, pinned first, then most recently
 * updated), and the writes that change it. Every write answers `null` on
 * success or the error it failed with; the screen picks its own copy.
 */

const ORDER = (a, b) => {
  if (a.status !== b.status) return a.status === "ACTIVE" ? -1 : 1;
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return a.updatedAt < b.updatedAt ? 1 : -1;
};

export default function useCoachMemories({ api = defaultApi, onCountChange, onSourceErased } = {}) {
  const [memories, setMemories] = useState(null);
  const [status, setStatus] = useState("idle");
  const callbacksRef = useRef({});
  callbacksRef.current = { onCountChange, onSourceErased };

  const apply = useCallback((update) => {
    setMemories((current) => (typeof update === "function" ? update(current || []) : update).slice().sort(ORDER));
  }, []);

  useEffect(() => {
    if (memories) callbacksRef.current.onCountChange?.(memories.filter((memory) => memory.status === "ACTIVE").length);
  }, [memories]);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const response = await api.getCoachMemories();
      apply(response.memories || []);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [api, apply]);

  const run = useCallback(async (task) => {
    try {
      await task();
      return null;
    } catch (error) {
      if (error?.code === "COACH_MEMORY_NOT_FOUND") load();
      return error || new Error("failed");
    }
  }, [load]);

  const create = useCallback((values) => run(async () => {
    const { memory } = await api.createCoachMemory(values);
    apply((list) => [memory, ...list]);
  }), [api, apply, run]);

  const update = useCallback((id, patch) => run(async () => {
    const { memory } = await api.updateCoachMemory(id, patch);
    apply((list) => list.map((item) => (item.id === id ? memory : item)));
  }), [api, apply, run]);

  const remove = useCallback((memory, { redactSource }) => run(async () => {
    try {
      await api.deleteCoachMemory(memory.id, { redactSource });
    } catch (error) {
      if (error?.code !== "COACH_MEMORY_NOT_FOUND") throw error;
    }
    apply((list) => list.filter((item) => item.id !== memory.id));
    if (redactSource && memory.sourceEventId) callbacksRef.current.onSourceErased?.(memory.sourceEventId);
  }), [api, apply, run]);

  const removeAll = useCallback(() => run(async () => {
    await api.deleteAllCoachMemories();
    apply([]);
  }), [api, apply, run]);

  return { memories, status, load, create, update, remove, removeAll };
}
